import { useEffect, useRef, useState } from 'react'
import { useQuery, useIsFetching, useQueryClient } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { Map as MaptilerMap, MapStyle, config, NavigationControl, GeolocateControl, addProtocol } from '@maptiler/sdk'
import { GeocodingControl } from '@maptiler/geocoding-control/maptilersdk'
import RulerControl from '@mapbox-controls/ruler'
import { PMTiles, Protocol } from 'pmtiles'

import {
  addLabResultPieImagesToMap,
  addMarkerImagesToMap,
  addSvgMarkerImagesToMap,
  addPulsingTrapImage,
  getProjectBounds,
} from '@App/components/Map/map.utils.js'
import createListPopup from '@App/components/Map/popups/ListPopup/ListPopup'
import { useAuth } from '@App/components/AuthProvider/AuthProvider'
import { fetchAllMissionDataTypes } from '@services/data.service'

import LayerControl from './controls/LayerControl/LayerControl'
import TerrainControl from './controls/TerrainControl/TerrainControl'
import ScreenshotControl from './controls/ScreenshotControl/ScreenshotControl'
import FeaturesDownloadControl from './controls/FeaturesDownloadControl/FeaturesDownloadControl'
import WebsocketStatusMapControl from './controls/WebsocketStatusControl/WebsocketStatusMapControl'
import sources from './map.sources.js'
import layers from './map.layers.js'

import '@maptiler/sdk/dist/maptiler-sdk.css'
import '@maptiler/geocoding-control/style.css'
import '@mapbox-controls/ruler/src/index.css'
import './map.styl'

const missionDataPmtilesSourceKey = 'osm-data'
const missionDataPmtilesUrl = 'https://nazka-tiles.s3.eu-west-1.amazonaws.com/pmtiles/global/osm-data.pmtiles'
const osmPopupLayerIds = ['environment-layer-osm', 'breeding-sites-layer-osm']

function getOsmLayerFilter(typeIds, dataTypeComparison, dataTypeBoundary) {
  return [
    'all',
    ['==', ['geometry-type'], 'Point'],
    ['==', ['get', 'source'], 'osm'],
    [dataTypeComparison, ['to-number', ['get', 'dataType']], dataTypeBoundary],
    ['in', ['to-string', ['get', 'dataType']], ['literal', (typeIds || []).map(String)]],
  ]
}

class PmtilesAliasSource {
  constructor(key, url) {
    this.key = key
    this.pmtiles = new PMTiles(url)
  }

  getKey() {
    return this.key
  }

  getBytes(...args) {
    return this.pmtiles.source.getBytes(...args)
  }
}

const pmtilesProtocol = new Protocol()
pmtilesProtocol.add(new PMTiles(new PmtilesAliasSource(missionDataPmtilesSourceKey, missionDataPmtilesUrl)))
let isPmtilesProtocolRegistered = false

function registerPmtilesProtocol() {
  if (isPmtilesProtocolRegistered) return

  try {
    addProtocol('pmtiles', pmtilesProtocol.tile)
  } catch (error) {
    if (!error.message?.includes('already registered')) throw error
  }

  isPmtilesProtocolRegistered = true
}

function Map({ map, setMap }) {
  const { pathname } = useLocation()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const mapRef = useRef(null)
  const prevMapState = useRef()
  const popupRef = useRef(null)
  const [style, setStyle] = useState('streets')
  const isFetching = useIsFetching({
    predicate: (query) => {
      return !['types'].includes(query.queryKey[0])
    },
  })

  const {
    activeProject,
    breedingSiteActiveTypeIds,
    environmentActiveTypeIds,
    sourcesActiveNames,
  } = useSelector(state => state.missionSetup)

  const { data: types } = useQuery({
    queryKey: ['types', 'all'],
    queryFn: fetchAllMissionDataTypes,
  })

  config.apiKey = import.meta.env.VITE_MAPTILER_KEY

  useEffect(() => {
    if (!types) return

    registerPmtilesProtocol()

    const maptilerMap = new MaptilerMap({
      container: mapRef.current,
      style: MapStyle[style.toUpperCase()],
      geolocateControl: false,
      navigationControl: false,
      canvasContextAttributes: {
        preserveDrawingBuffer: true,
      },
    })

    const layerControl = new LayerControl(maptilerMap, style, setStyle)
    maptilerMap.addControl(layerControl, 'bottom-left')

    const navigationControl = new NavigationControl(maptilerMap)
    maptilerMap.addControl(navigationControl, 'bottom-right')

    const geolocateControl = new GeolocateControl()
    maptilerMap.addControl(geolocateControl, 'bottom-right')

    const rulerControl = new RulerControl()
    maptilerMap.addControl(rulerControl, 'bottom-right')
    rulerControl?.container?.classList.add('maplibregl-ctrl', 'maplibregl-ctrl-group')

    const screenshotControl = new ScreenshotControl(maptilerMap)
    maptilerMap.addControl(screenshotControl, 'bottom-right')

    const featuresDownloadControl = new FeaturesDownloadControl(maptilerMap)
    maptilerMap.addControl(featuresDownloadControl, 'bottom-right')

    const terrainControl = new TerrainControl(maptilerMap)
    maptilerMap.addControl(terrainControl, 'bottom-right')

    maptilerMap.on('load', async () => {
      if (import.meta.env.VITE_NAZKA_ENV === 'development') {
        const wsStatusControl = new WebsocketStatusMapControl()
        maptilerMap.addControl(wsStatusControl, 'top-right')
      }

      Object.keys(sources).forEach((sourceId) => {
        maptilerMap.addSource(sourceId, sources[sourceId])
      })

      await addMarkerImagesToMap(maptilerMap)
      await addSvgMarkerImagesToMap(maptilerMap, types)
      await addLabResultPieImagesToMap(maptilerMap)
      await addPulsingTrapImage(maptilerMap, 'trap')
      await addPulsingTrapImage(maptilerMap, 'trap-cluster')
      layers(types).forEach(layer => maptilerMap.addLayer(layer))

      maptilerMap.on('moveend', () => {
        prevMapState.current = {
          zoom: maptilerMap.getZoom(),
          center: maptilerMap.getCenter(),
          pitch: maptilerMap.getPitch(),
          bearing: maptilerMap.getBearing(),
        }
      })
    })

    maptilerMap.once('load', () => {
      setMap(maptilerMap)
    })

    return () => maptilerMap.remove()
  }, [setMap, types, style])

  useEffect(() => {
    if (!map) return

    const bounds = getProjectBounds(activeProject)

    map.setMaxBounds(bounds)
    map.fitBounds(bounds, { animate: false })

    map.setPitch(0)
    map.setBearing(0)

    if (prevMapState.current) {
      map.setZoom(prevMapState.current.zoom)
      map.setCenter(prevMapState.current.center)
      map.setPitch(prevMapState.current.pitch)
      map.setBearing(prevMapState.current.bearing)
    }

    const [[minX, minY], , [maxX, maxY]] = activeProject.bbox.geometry.coordinates[0]
    const bbox = [minX, minY, maxX, maxY]

    const geocodingControl = new GeocodingControl({
      noResultsMessage: 'Location not found.',
      ...(activeProject?.country ? { country: activeProject?.country } : {}),
      ...(activeProject?.bbox ? { bbox } : {}),
    })

    map.addControl(geocodingControl, 'top-left')

    return () => {
      map.removeControl(geocodingControl)
    }
  }, [map, activeProject])

  useEffect(() => {
    if (!map || !sourcesActiveNames) return undefined

    const isTrapPlanner = pathname.includes('/mission-setup/trap-planner')
    const showEnvironmentOsm = pathname.includes('/mission-setup/environment') || isTrapPlanner
    const showBreedingSitesOsm = pathname.includes('/mission-setup/breeding-sites') || isTrapPlanner
    const osmLayerOpacity = isTrapPlanner ? 0.6 : 1
    const osmLayerAllowOverlap = !isTrapPlanner

    const setOsmLayerState = () => {
      const sourceIsVisible = sourcesActiveNames.includes('osm')

      if (map.getLayer('environment-layer-osm')) {
        map.setLayoutProperty(
          'environment-layer-osm',
          'visibility',
          sourceIsVisible && showEnvironmentOsm && environmentActiveTypeIds ? 'visible' : 'none',
        )
        map.setLayoutProperty('environment-layer-osm', 'icon-allow-overlap', osmLayerAllowOverlap)
        map.setPaintProperty('environment-layer-osm', 'icon-opacity', osmLayerOpacity)
        if (environmentActiveTypeIds) map.setFilter('environment-layer-osm', getOsmLayerFilter(environmentActiveTypeIds, '<', 12))
      }

      if (map.getLayer('breeding-sites-layer-osm')) {
        map.setLayoutProperty(
          'breeding-sites-layer-osm',
          'visibility',
          sourceIsVisible && showBreedingSitesOsm && breedingSiteActiveTypeIds ? 'visible' : 'none',
        )
        map.setLayoutProperty('breeding-sites-layer-osm', 'icon-allow-overlap', osmLayerAllowOverlap)
        map.setPaintProperty('breeding-sites-layer-osm', 'icon-opacity', osmLayerOpacity)
        if (breedingSiteActiveTypeIds) map.setFilter('breeding-sites-layer-osm', getOsmLayerFilter(breedingSiteActiveTypeIds, '>', 11))
      }
    }

    setOsmLayerState()
    map.once('idle', setOsmLayerState)

    return () => {
      map.off('idle', setOsmLayerState)
    }
  }, [map, pathname, sourcesActiveNames, environmentActiveTypeIds, breedingSiteActiveTypeIds])

  useEffect(() => {
    if (!map) return undefined

    const canvasContainer = document.querySelector('.maplibregl-canvas')

    const handleMouseEnter = () => {
      if (canvasContainer) canvasContainer.classList.add('pointer-cursor')
    }

    const handleMouseLeave = () => {
      if (canvasContainer) canvasContainer.classList.remove('pointer-cursor')
    }

    const handleClick = (event) => {
      const feature = event?.features?.[0]
      if (!feature) return

      if (popupRef.current) {
        popupRef.current.remove()
        popupRef.current = null
      }

      popupRef.current = createListPopup({
        map,
        coordinates: event.lngLat,
        props: feature.properties,
        queryClient,
        userId: user?.sub,
      })
    }

    const addHandlers = () => {
      const activeOsmPopupLayerIds = osmPopupLayerIds.filter(layerId => map.getLayer(layerId))
      if (activeOsmPopupLayerIds.length === 0) return

      map.on('mouseenter', activeOsmPopupLayerIds, handleMouseEnter)
      map.on('mouseleave', activeOsmPopupLayerIds, handleMouseLeave)
      map.on('click', activeOsmPopupLayerIds, handleClick)
    }

    addHandlers()
    map.once('idle', addHandlers)

    return () => {
      map.off('idle', addHandlers)
      map.off('mouseenter', osmPopupLayerIds, handleMouseEnter)
      map.off('mouseleave', osmPopupLayerIds, handleMouseLeave)
      map.off('click', osmPopupLayerIds, handleClick)
      if (canvasContainer) canvasContainer.classList.remove('pointer-cursor')
      if (popupRef.current) {
        popupRef.current.remove()
        popupRef.current = null
      }
    }
  }, [map, queryClient, user?.sub])

  return (
    <div ref={mapRef} className={`map${isFetching > 0 ? ' is-loading' : ''}`} />
  )
}

export default Map
